import { useEffect, useRef, useState } from "react";
import BgButtonOption from "../Buttons/BgButtonOption";
import ThemeSwitcher from "@/components/theme-switcher";
import { BASE_URL } from "../../../../config";
import { useAuthContext } from "@/context/auth/useAuthContext";
import Iconify from "../Iconify";
import H5 from "../headings/H5";
import Cookies from "js-cookie";
import { useTranslations } from "@/lib/static-intl";
import { useBrowserLanguage } from "@/hooks/useBrowserLanguage";
import {
  setUserLocaleClient,
  getBrowserLanguageClient,
  getUserLocaleClient,
} from "@/services/clientLocale";

const DropdownMenu = ({
  children,
  className,
  handleFeedbackModalOpen = () => { },
  handlePlaceRegisterModalOpen = () => { },
}) => {
  const t = useTranslations("header");
  const t2 = useTranslations("translate_box");
  const { browserLanguage } = useBrowserLanguage();

  const { user, handleLoginSession } = useAuthContext();
  const [active, setActive] = useState(false);
  const dropdownRef = useRef(null);

  const [currentLanguage, setCurrentLanguage] = useState("ko");
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);

  const themeIcons = [
    {
      id: "1",
      icon: "uil:setting",
      tooltip: "follow the system",
      theme: "system",
    },
    {
      id: "2",
      icon: "ph:sun",
      tooltip: "light mode",
      theme: "light",
    },
    { id: "3", icon: "ph:moon", tooltip: "dark mode", theme: "dark" },
  ];

  const languages = [
    {
       lang:
      currentLanguage === "ko"
        ? "시스템 언어"
        : currentLanguage === "en"
        ? "System Language"
        : t2("system_lang"),
      code: "browser",
    },
    {
      lang: "한국어",
      code: "ko",
    },
    {
      lang: "English",
      code: "en",
    },
  ];

  const performLogout = () => {
    handleLoginSession(user?.userId, "success", true);
    Cookies.remove("userId", { path: "/" });
    Cookies.remove("role", { path: "/" });
    localStorage.removeItem("jwtToken");
    window.open(`${BASE_URL}/logout`, "_self");
  };

  useEffect(() => {
    setCurrentLanguage(getUserLocaleClient());
  }, []);

  useEffect(() => {
    const outsideHandler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActive(false);
        setShowLanguageOptions(false);
      }
    };

    document.addEventListener("mousedown", outsideHandler);
    return () => {
      setShowLanguageOptions(false);
      document.removeEventListener("mousedown", outsideHandler);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("lang", currentLanguage);
  }, [currentLanguage]);



  return (
    <div className="relative z-20 h-full">
      <div className="hidden lg:block" onClick={() => setActive(!active)}>
        {children}
      </div>

      {active && (
        <div
          ref={dropdownRef}
          className={`absolute bottom-14 w-full rounded-xl drop-shadow-lg p-1 border dark:border-neutral-800 dark:bg-neutral-900 bg-dark_primary_label ${className}`}
        >
          {showLanguageOptions ? (
            <div className="flex flex-col gap-1 pb-2">
              <div className="flex items-center gap-1 p-2 border-b dark:border-brown mb-1">
                <button
                  type="button"
                  onClick={() => setShowLanguageOptions((prev) => !prev)}
                >
                  <Iconify
                    icon={"mingcute:left-line"}
                    className={
                      "!-mr-0.5 !-ml-2 dark:!text-dark_secondary_label !text-light_secondary_label"
                    }
                  />
                </button>
                <H5 className={"!text-nowrap !font-normal"}>
                  {t("change language")}
                </H5>
              </div>
              {languages?.map((lang, i) => (
                <BgButtonOption
                  key={i}
                  icon={
                    (lang.code === 'browser' && localStorage.getItem('isManuallySetLocale') !== 'true') ||
                      (lang.code !== 'browser' && currentLanguage === lang.code && localStorage.getItem('isManuallySetLocale') === 'true')
                      ? "material-symbols:check-rounded"
                      : null
                  }
                  onClick={async () => {
                    if (lang.code === 'browser') {
                      // Set to browser language detection mode
                      localStorage.removeItem('isManuallySetLocale');
                      localStorage.removeItem('hasAutoDetectedLanguage');
                      localStorage.removeItem('detectionRunThisSession');
                      localStorage.removeItem('manualLanguageChoice');

                      // Set to browser language immediately without reload
                      const browserLang = getBrowserLanguageClient();
                      setUserLocaleClient(browserLang);
                      setCurrentLanguage(browserLang);
                      localStorage.setItem('hotple-last-selected-language', browserLang);

                      // console.log(`[DropdownMenu] Browser language ${browserLang} detected, using system translation mode`);

                      // Trigger language change with system translation mode
                      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: browserLang, mode: 'browser' } }));
                    } else {
                      // Set to manual language immediately without reload
                      setUserLocaleClient(lang.code);
                      localStorage.setItem('isManuallySetLocale', 'true');
                      localStorage.setItem('manualLanguageChoice', lang.code);
                      localStorage.setItem('hasAutoDetectedLanguage', 'false');
                      localStorage.setItem('hotple-last-selected-language', lang.code);
                      setCurrentLanguage(lang.code);

                      // Trigger manual translation mode (for static manual translations)
                      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lang.code, mode: 'manual' } }));
                    }
                    
                    // Close dropdown after selection
                    setShowLanguageOptions(false);
                    setActive(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    {lang.icon && <span>{lang.icon}</span>}
                    <span>{lang.lang}</span>
                    {lang.code === 'browser' && (
                      <span className="text-xs text-gray-500">({browserLanguage.toUpperCase()})</span>
                    )}
                  </div>
                </BgButtonOption>
              ))}
            </div>
          ) : (
            <div className="flex flex-col divide-y dark:divide-brown">
              <div className="flex flex-col pb-1">
                <BgButtonOption
                  icon={"mingcute:right-line"}
                  onClick={() => setShowLanguageOptions((prev) => !prev)}
                >
                  {t("change language")}
                </BgButtonOption>
                <BgButtonOption
                  icon={"mingcute:right-line"}
                  onClick={handleFeedbackModalOpen}
                >
                  {t("give_feedback")}
                </BgButtonOption>
                <BgButtonOption
                  icon={"mingcute:right-line"}
                  onClick={handlePlaceRegisterModalOpen}
                >
                  {t("register_a_place")}
                </BgButtonOption>
              </div>

              {/* {
                user?.userId &&
                <LocationSwitcher t={t} locationIcons={locationIcons} />
              } */}

              <ThemeSwitcher t={t} themeIcons={themeIcons} />

              {user?.userId && (
                <BgButtonOption
                  onClick={performLogout}
                  icon={"mdi-light:logout"}
                >
                  {t("logout")}
                </BgButtonOption>
              )}
            </div>
          )}
        </div>
      )}
      <div className="block lg:hidden" onClick={() => setActive(!active)}>
        {children}
      </div>
    </div>
  );
};

export default DropdownMenu;
